/* ============================================================================
 * Brand-палитра: из одного основного цвета (hex #RRGGBB) считаем производные
 * оттенки для акцентной темы Mini App. Без зависимостей.
 *
 * Партнёр задаёт ОДИН цвет («Основной цвет Mini App»), всё голубое в интерфейсе
 * (кнопки, иконки, чипы, активный таб, прогресс-бары, свечение) перекрашивается
 * в него. Функциональные цвета (зелёный/красный/серый статусы) не трогаем.
 * ========================================================================== */

export const HEX6 = /^#[0-9a-fA-F]{6}$/;

/** Фон карточки мини-аппа (--color-card) — база для проверки читаемости accent. */
export const CARD_BACKGROUND = "#1a1b1e";

interface Rgb {
  r: number;
  g: number;
  b: number;
}

interface Hsl {
  h: number; // 0..360
  s: number; // 0..1
  l: number; // 0..1
}

export function hexToRgb(hex: string): Rgb {
  const v = hex.replace("#", "");
  return {
    r: parseInt(v.slice(0, 2), 16),
    g: parseInt(v.slice(2, 4), 16),
    b: parseInt(v.slice(4, 6), 16),
  };
}

/** "51 144 236" — для rgb(var(--brand-primary-rgb) / α). */
export function hexToRgbChannels(hex: string): string {
  const { r, g, b } = hexToRgb(hex);
  return `${r} ${g} ${b}`;
}

function rgbToHsl({ r, g, b }: Rgb): Hsl {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case rn:
        h = (gn - bn) / d + (gn < bn ? 6 : 0);
        break;
      case gn:
        h = (bn - rn) / d + 2;
        break;
      default:
        h = (rn - gn) / d + 4;
    }
    h *= 60;
  }
  return { h, s, l };
}

function hslToHex({ h, s, l }: Hsl): string {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let rp = 0;
  let gp = 0;
  let bp = 0;
  if (h < 60) [rp, gp, bp] = [c, x, 0];
  else if (h < 120) [rp, gp, bp] = [x, c, 0];
  else if (h < 180) [rp, gp, bp] = [0, c, x];
  else if (h < 240) [rp, gp, bp] = [0, x, c];
  else if (h < 300) [rp, gp, bp] = [x, 0, c];
  else [rp, gp, bp] = [c, 0, x];
  const to = (v: number): string =>
    Math.round((v + m) * 255)
      .toString(16)
      .padStart(2, "0");
  return `#${to(rp)}${to(gp)}${to(bp)}`;
}

const clampL = (l: number): number => Math.min(1, Math.max(0, l));

/** Сдвиг светлоты на delta (в долях 0..1). */
export function shiftLightness(hex: string, delta: number): string {
  const hsl = rgbToHsl(hexToRgb(hex));
  return hslToHex({ ...hsl, l: clampL(hsl.l + delta) });
}

/* ---------------------------------------------------------------------------
 * Контраст (WCAG 2.1). Партнёр волен выбрать любой hex, включая жёлтый, лайм
 * или белый. Раньше цвет текста на кнопке был прибит белым (`.calm-cta`), и на
 * светлом бренде надпись «Оплатить» уходила в 1.1–1.6:1 — то есть исчезала.
 * Обратный край не лучше: почти чёрный бренд как ЦВЕТ ТЕКСТА (чипы, иконки)
 * пропадал на тёмной карточке. Поэтому оба конца спектра теперь считаются.
 * ------------------------------------------------------------------------- */

/** Относительная яркость по WCAG (0 — чёрный, 1 — белый). */
export function relativeLuminance(hex: string): number {
  const { r, g, b } = hexToRgb(hex);
  const ch = (v: number): number => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * ch(r) + 0.7152 * ch(g) + 0.0722 * ch(b);
}

/** Коэффициент контраста двух цветов: от 1:1 (одинаковые) до 21:1. */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/**
 * Цвет надписи на заливке: белый, пока он держит хотя бы `keepWhiteAbove`.
 *
 * Не «что контрастнее», а именно так — с запасом в пользу белого. Иначе
 * дефолтный голубой (#3390EC даёт белому 3.55:1, чёрному 5.92:1) перевернулся
 * бы в чёрный текст, и привычная кнопка Telegram сменила бы вид у всех ботов,
 * где партнёр цвет вообще не трогал. Порог 3:1 — граница WCAG для крупного и
 * жирного текста (наши CTA как раз bold); ниже неё белый уже не читается, и
 * там переключаемся на чёрный. Так чинится провал (жёлтый давал 1.6:1), а
 * привычный вид остаётся на месте.
 */
export function readableOn(background: string, keepWhiteAbove = 3): string {
  return contrastRatio(background, "#ffffff") >= keepWhiteAbove ? "#ffffff" : "#000000";
}

/**
 * Осветляет цвет, пока он не станет читаемым на тёмном фоне мини-аппа.
 * Нужно для accent: им красится текст чипов и иконки поверх карточки #1a1b1e.
 * Порог 3:1 — как для крупного текста и графических элементов (WCAG 1.4.11).
 */
export function ensureReadableOnDark(hex: string, background: string, min = 3): string {
  let out = hex;
  // Шаг 2% светлоты, максимум 50 итераций — до чистого белого хватает с запасом.
  for (let i = 0; i < 50 && contrastRatio(out, background) < min; i++) {
    const next = shiftLightness(out, 0.02);
    if (next === out) break; // упёрлись в белый — дальше некуда
    out = next;
  }
  return out;
}

export interface BrandPalette {
  primary: string;
  accent: string;
  ctaFrom: string;
  ctaTo: string;
  primaryRgb: string;
  accentRgb: string;
  /** Цвет текста на CTA-градиенте — считается по его середине. */
  ctaForeground: string;
  /** Цвет текста на сплошной заливке --color-primary. */
  primaryForeground: string;
}

/**
 * Из основного цвета производит акцентную палитру.
 * Соотношения подобраны по дефолтному голубому (#3390EC → accent #4ea4f0,
 * cta #4a9bf0…#2f7fd6): accent чуть светлее, cta — лёгкий вертикальный градиент.
 */
export function deriveBrandPalette(hex: string): BrandPalette {
  const ctaFrom = shiftLightness(hex, 0.05);
  const ctaTo = shiftLightness(hex, -0.1);

  // Текст на кнопке читается против СЕРЕДИНЫ градиента: по верхнему стопу он
  // казался бы контрастнее, чем есть на самом деле у нижнего края кнопки.
  const ctaMid = shiftLightness(hex, -0.025);

  // accent красит текст и иконки поверх тёмной карточки — тёмный бренд там
  // сливается с фоном, поэтому светлоту поднимаем до порога читаемости.
  const accent = ensureReadableOnDark(shiftLightness(hex, 0.08), CARD_BACKGROUND);

  return {
    primary: hex,
    accent,
    ctaFrom,
    ctaTo,
    primaryRgb: hexToRgbChannels(hex),
    accentRgb: hexToRgbChannels(accent),
    ctaForeground: readableOn(ctaMid),
    primaryForeground: readableOn(hex),
  };
}
